import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Users,
  DollarSign,
  Activity,
  Plus,
  Power,
  RefreshCw,
  Lock,
  Eye,
  EyeOff
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_CODE = "VEXL-SUPERADMIN-2024";

const SuperAdminPortal = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminCode, setAdminCode] = useState("");
  const [showCode, setShowCode] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  
  const [influencers, setInfluencers] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newInfluencer, setNewInfluencer] = useState({
    name: "",
    brand_name: "",
    email: ""
  });

  // Brand colors
  const primaryColor = "#CDF22B";
  const secondaryColor = "#1E45FB";
  
  // Check if already authenticated
  useEffect(() => {
    const savedCode = sessionStorage.getItem("vexl_admin_code");
    if (savedCode === ADMIN_CODE) {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    
    // Verify the code by making a test API call
    try {
      await axios.get(`${API}/admin/metrics`, {
        headers: { "X-Admin-Code": adminCode }
      });
      
      // If successful, save to session and authenticate
      sessionStorage.setItem("vexl_admin_code", adminCode);
      setIsAuthenticated(true);
      toast.success("Acceso concedido");
    } catch (error) {
      toast.error("Código de acceso incorrecto");
    }
    setAuthLoading(false);
  };
  
  const handleLogout = () => {
    sessionStorage.removeItem("vexl_admin_code");
    setIsAuthenticated(false);
    setAdminCode("");
  };

  const fetchData = async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const savedCode = sessionStorage.getItem("vexl_admin_code");
      const [infRes, metRes] = await Promise.all([
        axios.get(`${API}/admin/influencers`, {
          headers: { "X-Admin-Code": savedCode }
        }),
        axios.get(`${API}/admin/metrics`, {
          headers: { "X-Admin-Code": savedCode }
        })
      ]);
      setInfluencers(infRes.data);
      setMetrics(metRes.data);
    } catch (error) {
      toast.error("Error loading data");
      if (error.response?.status === 403) {
        handleLogout();
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
      
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const handleCreateInfluencer = async (e) => {
    e.preventDefault();
    try {
      const savedCode = sessionStorage.getItem("vexl_admin_code");
      const res = await axios.post(`${API}/admin/influencers`, newInfluencer, {
        headers: { "X-Admin-Code": savedCode }
      });
      setInfluencers([...influencers, res.data]);
      setCreateOpen(false);
      setNewInfluencer({ name: "", brand_name: "", email: "" });
      toast.success("Influencer created successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error creating influencer");
    }
  };

  const toggleStatus = async (id) => {
    try {
      const savedCode = sessionStorage.getItem("vexl_admin_code");
      await axios.patch(
        `${API}/admin/influencers/${id}/status`,
        {},
        { headers: { "X-Admin-Code": savedCode } }
      );
      fetchData();
      toast.success("Status updated");
    } catch (error) {
      toast.error("Error updating status");
    }
  };
  
  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-vexl-bg flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <img src="/logo.png" alt="VEXL.fit" className="h-16 w-auto mx-auto mb-4" />
            <h1 className="font-display text-2xl font-bold text-white">Admin Portal</h1>
            <p className="text-vexl-text-secondary mt-2">Ingresa tu código de acceso</p>
          </div>
          
          {/* Login Card */}
          <div className="bg-vexl-surface border border-vexl-border rounded-2xl p-8">
            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label className="text-sm text-vexl-text-secondary mb-2 block">Código de Admin</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-vexl-text-secondary" />
                  <Input
                    type={showCode ? "text" : "password"}
                    value={adminCode}
                    onChange={(e) => setAdminCode(e.target.value)}
                    className="pl-12 pr-12 h-14 bg-vexl-bg border-vexl-border rounded-xl text-white"
                    placeholder="VEXL-XXXXX-XXXX"
                    required
                    data-testid="admin-code-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCode(!showCode)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-vexl-text-secondary hover:text-white"
                  >
                    {showCode ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              
              <Button
                type="submit"
                disabled={authLoading || !adminCode}
                className="w-full h-14 rounded-xl font-bold text-lg bg-[#CDF22B] text-vexl-bg hover:bg-[#b8dc1f]"
                data-testid="admin-login-btn"
              >
                {authLoading ? "Verificando..." : "Acceder"}
              </Button>
            </form>
            
            <p className="text-center text-sm text-vexl-text-secondary mt-6">
              ¿No tienes acceso?{" "}
              <a href="/" className="text-[#CDF22B] hover:underline">Volver al inicio</a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-vexl-bg">
      {/* Header */}
      <header className="bg-vexl-surface border-b border-vexl-border px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="VEXL.fit" className="h-10 w-auto" />
            <div>
              <h1 className="font-display text-xl font-bold text-white">VEXL.fit</h1>
              <p className="text-xs text-vexl-text-secondary uppercase tracking-wider">Super Admin Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              data-testid="refresh-btn"
              variant="outline"
              size="sm"
              onClick={fetchData}
              className="border-vexl-border text-vexl-text-secondary hover:text-[#CDF22B] hover:border-[#CDF22B] rounded-full"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button
              data-testid="logout-btn"
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="border-red-500/50 text-red-400 hover:bg-red-500/10 rounded-full"
            >
              <Power className="w-4 h-4 mr-2" />
              Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="metric-card" data-testid="metric-influencers">
            <div className="flex items-center justify-between">
              <div>
                <p className="metric-value">{metrics?.total_influencers || 0}</p>
                <p className="metric-label">Total Influencers</p>
              </div>
              <Users className="w-10 h-10 text-[#CDF22B]/30" />
            </div>
          </div>
          <div className="metric-card" data-testid="metric-active">
            <div className="flex items-center justify-between">
              <div>
                <p className="metric-value">{metrics?.active_influencers || 0}</p>
                <p className="metric-label">Active</p>
              </div>
              <Activity className="w-10 h-10 text-[#1E45FB]/50" />
            </div>
          </div>
          <div className="metric-card" data-testid="metric-users">
            <div className="flex items-center justify-between">
              <div>
                <p className="metric-value">{metrics?.total_users || 0}</p>
                <p className="metric-label">Total Users</p>
              </div>
              <Users className="w-10 h-10 text-[#CDF22B]/30" />
            </div>
          </div>
          <div className="metric-card" data-testid="metric-mrr">
            <div className="flex items-center justify-between">
              <div>
                <p className="metric-value">${metrics?.mrr || 0}</p>
                <p className="metric-label">MRR</p>
              </div>
              <DollarSign className="w-10 h-10 text-[#CDF22B]/30" />
            </div>
          </div>
        </div>

        {/* Influencers Table */}
        <div className="card-dark overflow-hidden">
          <div className="flex items-center justify-between p-6 border-b border-vexl-border">
            <h2 className="font-display text-lg font-semibold text-white">Influencers</h2>
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="create-influencer-btn"
                  className="bg-[#CDF22B] text-vexl-bg hover:bg-[#b8dc1f] rounded-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Influencer
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-vexl-surface border-vexl-border rounded-2xl">
                <DialogHeader>
                  <DialogTitle className="font-display text-white">Create Influencer</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateInfluencer} className="space-y-4 mt-4">
                  <div>
                    <label className="text-sm text-vexl-text-secondary mb-1 block">Name</label>
                    <Input
                      data-testid="influencer-name-input"
                      value={newInfluencer.name}
                      onChange={(e) => setNewInfluencer({ ...newInfluencer, name: e.target.value })}
                      className="input-dark"
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm text-vexl-text-secondary mb-1 block">Brand Name</label>
                    <Input
                      data-testid="influencer-brand-input"
                      value={newInfluencer.brand_name}
                      onChange={(e) => setNewInfluencer({ ...newInfluencer, brand_name: e.target.value })}
                      className="input-dark"
                      placeholder="fitpro"
                      required
                    />
                    <p className="text-xs text-vexl-text-secondary mt-1">
                      URL: vexl.fit/{newInfluencer.brand_name.toLowerCase().replace(/\s/g, "-") || "brand"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-vexl-text-secondary mb-1 block">Email</label>
                    <Input
                      data-testid="influencer-email-input"
                      type="email"
                      value={newInfluencer.email}
                      onChange={(e) => setNewInfluencer({ ...newInfluencer, email: e.target.value })}
                      className="input-dark"
                      placeholder="john@example.com"
                      required
                    />
                  </div>
                  <Button
                    data-testid="submit-influencer-btn"
                    type="submit"
                    className="w-full bg-[#CDF22B] text-vexl-bg hover:bg-[#b8dc1f] rounded-full"
                  >
                    Create Influencer
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin w-8 h-8 border-2 border-vexl-primary border-t-transparent rounded-full mx-auto" />
            </div>
          ) : influencers.length === 0 ? (
            <div className="p-12 text-center text-vexl-text-secondary">
              No influencers yet. Create one to get started.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table-dark" data-testid="influencers-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Brand</th>
                    <th>Email</th>
                    <th>Users</th>
                    <th>Status</th>
                    <th>Access Code</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {influencers.map((inf) => (
                    <tr key={inf.id} data-testid={`influencer-row-${inf.id}`}>
                      <td className="font-medium text-white">{inf.name}</td>
                      <td>
                        <span className="font-display text-[#CDF22B]">{inf.brand_name}</span>
                      </td>
                      <td className="text-vexl-text-secondary">{inf.email}</td>
                      <td>
                        <span className="font-display text-white">{inf.total_users}</span>
                      </td>
                      <td>
                        <span className={`badge ${inf.subscription_status === "active" ? "badge-success" : "badge-error"}`}>
                          {inf.subscription_status}
                        </span>
                      </td>
                      <td>
                        <code className="text-xs bg-vexl-bg px-2 py-1 text-vexl-text-secondary">
                          {inf.access_code.slice(0, 8)}...
                        </code>
                      </td>
                      <td>
                        <Button
                          data-testid={`toggle-status-${inf.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleStatus(inf.id)}
                          className={inf.subscription_status === "active" ? "text-red-400 hover:text-red-300" : "text-[#CDF22B] hover:text-[#b8dc1f]"}
                        >
                          <Power className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Access Codes Info */}
        {influencers.length > 0 && (
          <div className="mt-8 card-dark p-6">
            <h3 className="font-display text-lg font-semibold text-white mb-4">Access Codes Reference</h3>
            <div className="space-y-4">
              {influencers.map((inf) => (
                <div key={inf.id} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 p-4 bg-vexl-bg border border-vexl-border rounded-xl">
                  <span className="font-medium text-white w-32">{inf.name}</span>
                  <div className="flex-1 space-y-1">
                    <p className="text-xs text-vexl-text-secondary">
                      Influencer Dashboard: <code className="text-[#CDF22B]">/influencer/{inf.access_code}</code>
                    </p>
                    <p className="text-xs text-vexl-text-secondary">
                      User Registration Code: <code className="text-[#1E45FB]">{inf.user_access_code}</code>
                    </p>
                    <p className="text-xs text-vexl-text-secondary">
                      Client App URL: <code className="text-[#CDF22B]">/{inf.brand_name}</code>
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default SuperAdminPortal;
